import { useState, useEffect } from "react";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import api from "../../utils/api";
import MetricTooltip from "../../components/shared/MetricTooltip/MetricTooltip";
import { METRIC_TOOLTIPS } from "../../components/shared/MetricTooltip/metricCopy";
import "./ConversionCyclesTab.css";

/* (i) + (?) tooltip pair using a metricCopy key */
function TT({ k }) {
  const m = METRIC_TOOLTIPS[k];
  if (!m) return null;
  return (
    <>
      <MetricTooltip type="info" content={m.info} showAccountingNote={m.accountingNote} metricKey={k} testKey={k} />
      <MetricTooltip type="help" content={m.help} testKey={k} />
    </>
  );
}

/* Approved palette only - banned color families absent */
const C = {
  navy: "#1A202C",
  slate: "#64748B",
  orange: "#F5841F",    // DPO accent
  gold: "#D4A017",      // DSO accent
  purple: "#8B5CF6",    // CCC accent
  indigo: "#6366F1",
  blue: "#3B82F6",
};

function _num(v) { const n = typeof v === "number" ? v : Number(v); return Number.isFinite(n) ? n : 0; }
function fmtMoney(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(_num(n)); }

/* Trend chip - orange ▲ / indigo ▼ */
function Trend({ value, suffix = "", forceDown = false }) {
  const v = _num(value);
  const isDown = forceDown || v < 0;
  const color = isDown ? C.indigo : C.orange;
  const Arrow = isDown ? TrendingDown : TrendingUp;
  return (
    <span style={{ color, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Arrow size={13} style={{ color }} />
      {isDown ? "" : "+"}{v}% {suffix}
    </span>
  );
}

/* Inline trend (smaller, no suffix) */
function InlineTrend({ value, forceDown = false }) {
  const v = _num(value);
  const isDown = forceDown || v < 0;
  const color = isDown ? C.indigo : C.orange;
  const Arrow = isDown ? TrendingDown : TrendingUp;
  return (
    <span style={{ color, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Arrow size={12} style={{ color }} />
      {isDown ? "" : "+"}{v}%
    </span>
  );
}

function pctBadgeClass(pct) {
  const v = _num(pct);
  if (v >= 50) return "cv-badge cv-badge-a";
  if (v >= 10) return "cv-badge cv-badge-b";
  return "cv-badge cv-badge-c";
}
function pctBadgeIconColor(pct) {
  const v = _num(pct);
  if (v >= 50) return C.orange;
  if (v >= 10) return C.blue;
  return C.slate;
}

export default function ConversionCyclesTab({ dateRange }) {
  const [conversion, setConversion] = useState(null);
  const [bsAnalysis, setBsAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange?.start) params.set("start_date", dateRange.start);
    if (dateRange?.end) params.set("end_date", dateRange.end);
    const qs = params.toString();
    /* Two existing endpoints, untouched: conversion-cycles + QuickBooks ratios-detail (balance_sheet_analysis) */
    Promise.all([
      api.get(`/valuation/conversion-cycles${qs ? "?" + qs : ""}`).then(r => r.data).catch(() => null),
      api.get(`/dashboard-data/ratios-detail${qs ? "?" + qs : ""}`).then(r => r.data?.balance_sheet_analysis || []).catch(() => []),
    ]).then(([cv, bs]) => { setConversion(cv); setBsAnalysis(bs); setLoading(false); });
  }, [dateRange]);

  if (loading || !conversion) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: C.blue }} /></div>;

  /* Row 1 metrics - spec values, with Cash Conversion Cycle wired to backend */
  const row1 = [
    { label: "Market-to-Book Ratio", value: "6.9%",  change: 14.3, hasMtd: true,  inline: false, forceDown: false, ttKey: null },
    { label: "Market Cap to Ratio",  value: "50%",   change: 14.9, hasMtd: true,  inline: false, forceDown: false, ttKey: null },
    { label: "Cash Conversion Cycle",value: `${conversion.cash_conversion_cycle} days`, change: 96, hasMtd: false, inline: true, forceDown: false, ttKey: "cashConversionCycle" },
    { label: "Retained Earnings",    value: "$500",  change: 14.8, hasMtd: false, inline: true, forceDown: true, ttKey: null },
  ];

  /* Row 2 left: 4 conversion-cycle rows wired to backend days - each labeled with its accent color */
  const ccRows = [
    { label: "Accounts Receivable Days (DSO)", value: `${conversion.days_sales_outstanding} days`,  pct: 68, accent: C.gold, ttKey: "dso" },
    { label: "Accounts Payable Days (DPO)",    value: `${conversion.days_payable_outstanding} days`, pct: 68, accent: C.orange, ttKey: "dpo" },
    { label: "Inventory Days",                  value: `${conversion.days_inventory_outstanding} days`, pct: 68, accent: C.blue, ttKey: "daysInventoryOutstanding" },
    { label: "Cash Conversion Cycle (CCC)",     value: `${conversion.cash_conversion_cycle} days`, pct: 58, accent: C.purple, ttKey: "cashConversionCycle" },
  ];

  /* Row 2 right card A: cash metric tiles */
  const tiles = [
    { label: "Ending cash",          value: "$145,250", trendText: "+8.5% vs last month", ttKey: "endingCash" },
    { label: "Days cash on hand",    value: "45 days",  trendText: "+3 days vs last month", ttKey: "daysCashOnHand" },
    { label: "Net cash flow (monthly)", value: "$28,340", trendText: "+12.3% vs last month", ttKey: "netCashFlow" },
  ];

  return (
    <div className="cv-page" data-testid="conversion-cycles-tab">

      {/* ===== ROW 1: 4-column metric strip ===== */}
      <div>
        <div className="cv-section-label">Ratios</div>
        <div className="cv-card" data-testid="cv-row-1">
          <div className="cv-row-1-grid">
            {row1.map((r, i) => (
              <div key={i} className="cv-cell" data-testid={`cv-row1-cell-${i}`}>
                {r.hasMtd ? (
                  <>
                    <div className="cv-cell-top">
                      <span className="cv-cell-name">
                        {r.label}
                        {r.ttKey && <TT k={r.ttKey} />}
                      </span>
                      <span className="cv-mtd-pill">MTD</span>
                    </div>
                    <div className="cv-cell-trend"><Trend value={r.change} /></div>
                    <div className="cv-cell-value">{r.value}</div>
                  </>
                ) : (
                  <>
                    <div className="cv-cell-label">
                      {r.label}
                      {r.ttKey && <TT k={r.ttKey} />}
                    </div>
                    <div className="cv-cell-value-row">
                      <span className="cv-cell-value">{r.value}</span>
                      <span className="cv-cell-inline-trend"><InlineTrend value={r.change} forceDown={r.forceDown} /></span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== ROW 2: 45/55 split ===== */}
      <div className="cv-row-2">

        {/* LEFT (45%): Conversion Cycle card */}
        <div className="cv-card" data-testid="conversion-cycle-card">
          <div className="cv-card-title-row">
            <div>
              <div className="cv-card-title">Conversion Cycle</div>
              <div className="cv-card-subtitle">Cash Conversion Cycle</div>
            </div>
            <span className="cv-mtd-outline">MTD</span>
          </div>
          <div className="cv-cc-divider-top" />
          {ccRows.map((row, i) => (
            <div key={i} className="cv-cc-row" data-testid={`cc-row-${i}`}>
              <span className="cv-cc-label" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.accent, display: "inline-block" }} />
                {row.label}
                {row.ttKey && <TT k={row.ttKey} />}
              </span>
              <span className="cv-cc-value" style={{ color: row.accent }}>{row.value}</span>
              <span className={pctBadgeClass(row.pct)}>
                {row.pct}%
                <TrendingUp size={12} style={{ color: pctBadgeIconColor(row.pct) }} />
              </span>
            </div>
          ))}
          {/* Legend */}
          <div className="cv-cc-legend" data-testid="conversion-cycle-legend">
            <span className="cv-legend-item"><span className="cv-legend-dot" style={{ background: C.gold }} />DSO = Days Sales Outstanding</span>
            <span className="cv-legend-item"><span className="cv-legend-dot" style={{ background: C.orange }} />DPO = Days Payable Outstanding</span>
            <span className="cv-legend-item"><span className="cv-legend-dot" style={{ background: C.purple }} />CCC = Cash Conversion Cycle</span>
          </div>
        </div>

        {/* RIGHT (55%): two stacked cards */}
        <div className="cv-col-right">

          {/* Right Card A: 3 cash metric tiles, no header */}
          <div className="cv-card" data-testid="cash-metrics">
            <div className="cv-tiles">
              {tiles.map((t, i) => (
                <div key={i} className="cv-tile" data-testid={`cash-tile-${i}`}>
                  <div className="cv-tile-label">
                    {t.label}
                    {t.ttKey && <TT k={t.ttKey} />}
                  </div>
                  <div className="cv-tile-value">{t.value}</div>
                  <div className="cv-tile-trend">
                    <TrendingUp size={13} style={{ color: C.orange }} />
                    <span style={{ color: C.orange }}>{t.trendText}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Card B: Balance Sheet Analysis (QuickBooks data - unchanged hook) */}
          <div>
            <div className="cv-card" data-testid="balance-sheet-analysis">
              <div className="cv-card-title-row" style={{ alignItems: "center" }}>
                <div className="cv-card-title">Balance Sheet Analysis</div>
                <span className="cv-mtd-outline">MTD</span>
              </div>
              {bsAnalysis.map((row, i) => (
                <div key={i} className="cv-bs-row" data-testid={`bs-row-${i}`}>
                  <span className="cv-bs-label">{row.label}</span>
                  <span className="cv-bs-value">MTD {fmtMoney(row.mtd)}</span>
                  <span className="cv-bs-value">{typeof row.value === "number" && row.value > 100 ? fmtMoney(row.value) : `${row.value}%`}</span>
                  <div className="cv-bs-pct">
                    <span className={pctBadgeClass(row.change)}>
                      {row.change}%
                      <TrendingUp size={12} style={{ color: pctBadgeIconColor(row.change) }} />
                    </span>
                  </div>
                </div>
              ))}
              {bsAnalysis.length === 0 && (
                <div style={{ fontSize: 12, color: C.slate, padding: "12px 0" }}>No QuickBooks data available.</div>
              )}
            </div>
            <div className="cv-qb-attr">Data source: QuickBooks</div>
          </div>
        </div>
      </div>
    </div>
  );
}
